import { useState } from "react";
import { t } from "@/i18n-js/instance";
import { PageMetaData } from "@/react/components/Layout/PageMetaData";
import { useContactsSearchEnabledForMembersAndSpaces } from "@/react/hooks/useContactsSearchEnabledForMembersAndSpaces";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";
import { useActiveView } from "@/react/hooks/utils/useActiveView";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { CommunityMembers } from "@circle-react-shared/MembersList/CommunityMembers";
import { Filters } from "@circle-react-shared/MembersList/Filters";
import { VIEWS } from "@circle-react-shared/MembersList/constants";
import { useNavigationLinks } from "@circle-react-uikit/HeaderV3/NavigationBar/hooks/useNavigationLinks";
import { StandardLayout } from "../Layout/StandardLayout";
import { AudienceSearchContext } from "../shared/MemberFilters/AudienceSearchContext";
import { ViewTypes } from "../shared/MembersList/CommunityMembers/ListView/CommunityMember/MemberActions";
import { Header } from "./Header";
import type { MemberDirectorySettings } from "./constants";

const bodyClassNames = "view-members-directory";

export interface MemberDirectoryContentProps {
  memberDirectorySettings: MemberDirectorySettings;
}

export const MemberDirectoryContent = ({
  memberDirectorySettings,
}: MemberDirectoryContentProps) => {
  const { memberDirectoryLink } = useNavigationLinks();
  const [memberCount, setMemberCount] = useState(0);

  const isContactsSearchEnabled = useContactsSearchEnabledForMembersAndSpaces();

  const [hasMobileFiltersTransition, setHasMobileFiltersTransition] =
    useState(false);

  const [activeView, setActiveView]: any[] = useActiveView({
    defaultCommunityView: memberDirectorySettings.view,
    views: VIEWS,
  });

  const isConnectEnabled = useIsConnectEnabled();

  const memberDirectoryTitle =
    memberDirectoryLink?.label || t("members_directory.title");

  return (
    <AudienceSearchContext.Provider
      value={{ usage: isContactsSearchEnabled ? "contacts" : "members" }}
    >
      <Header
        memberCount={memberCount}
        activeView={activeView}
        setActiveView={setActiveView}
        setShouldRenderMobileFiltersTransition={setHasMobileFiltersTransition}
        headerTitle={memberDirectoryTitle}
      />
      <StandardLayout.Body
        hasPaddingTopMobile={false}
        className={classNames({
          "bg-primary": isConnectEnabled,
        })}
      >
        <PageMetaData
          title={memberDirectoryTitle}
          bodyClassNames={bodyClassNames}
        />

        <StandardLayout.Content className={classNames("md:!min-w-0")}>
          <Filters
            shouldRenderMobileFiltersTransition={hasMobileFiltersTransition}
            shouldBeHiddenOnMd
            shouldShowAdminFilters
          />
          <CommunityMembers
            setMemberCount={setMemberCount}
            scopeResults={memberDirectorySettings.scope}
            defaultSort={memberDirectorySettings.defaultSort}
            shouldShowHiddenMemberTooltip
            viewType={ViewTypes.MEMBER_DIRECTORY}
            activeView={activeView}
          />
        </StandardLayout.Content>
      </StandardLayout.Body>
    </AudienceSearchContext.Provider>
  );
};
